import { tAuto } from "@/lib/tAuto";
/**
 * Browser / device compatibility detection (permissive mode).
 *
 * We only block truly ancient environments that would fail to render
 * the app correctly. Everything modern (last ~5 years) passes.
 *
 * Thresholds (permissive):
 *   iOS         >= 13
 *   Android     >= 7
 *   Chrome      >= 90
 *   Safari      >= 14
 *   Firefox     >= 90
 *   Edge        >= 90
 */

export interface CompatInfo {
  compatible: boolean;
  browser: string;
  os: string;
  version: string;
  reason?: string;
}

function featureChecks(): string | null {
  if (typeof window === "undefined") return null;
  if (typeof Promise === "undefined") return "Promise";
  if (typeof fetch === "undefined") return "fetch";
  if (typeof IntersectionObserver === "undefined") return "IntersectionObserver";
  if (typeof localStorage === "undefined") return "localStorage";
  if (!("CSS" in window) || typeof (window as any).CSS.supports !== "function")
    return "CSS.supports";
  return null;
}

function parseUA(ua: string) {
  let browser = "Desconhecido";
  let os = "Desconhecido";
  let version = "0";

  // OS detection
  let osMajor = 0;
  if (/iPhone|iPad|iPod/.test(ua)) {
    os = "iOS";
    const m = ua.match(/OS (\d+)[_\.](\d+)/);
    if (m) {
      osMajor = parseInt(m[1], 10);
      version = `${m[1]}.${m[2]}`;
    }
  } else if (/Android/.test(ua)) {
    os = "Android";
    const m = ua.match(/Android (\d+)\.?(\d+)?/);
    if (m) {
      osMajor = parseInt(m[1], 10);
      version = m[2] ? `${m[1]}.${m[2]}` : m[1];
    }
  } else if (/Windows/.test(ua)) {
    os = "Windows";
  } else if (/Mac OS X/.test(ua)) {
    os = "macOS";
  } else if (/Linux/.test(ua)) {
    os = "Linux";
  }

  // Browser detection (order matters: Edge before Chrome, Chrome before Safari)
  let browserMajor = 0;
  if (/Edg\//.test(ua)) {
    browser = "Edge";
    const m = ua.match(/Edg\/(\d+)/);
    if (m) { browserMajor = parseInt(m[1], 10); version = m[1]; }
  } else if (/OPR\//.test(ua) || /Opera/.test(ua)) {
    browser = "Opera";
    const m = ua.match(/OPR\/(\d+)/);
    if (m) { browserMajor = parseInt(m[1], 10); version = m[1]; }
  } else if (/Firefox\//.test(ua)) {
    browser = "Firefox";
    const m = ua.match(/Firefox\/(\d+)/);
    if (m) { browserMajor = parseInt(m[1], 10); version = m[1]; }
  } else if (/Chrome\//.test(ua) && !/Edg/.test(ua)) {
    browser = "Chrome";
    const m = ua.match(/Chrome\/(\d+)/);
    if (m) { browserMajor = parseInt(m[1], 10); version = m[1]; }
  } else if (/Safari\//.test(ua) && /Version\//.test(ua)) {
    browser = "Safari";
    const m = ua.match(/Version\/(\d+)/);
    if (m) { browserMajor = parseInt(m[1], 10); version = m[1]; }
  }

  return { browser, os, version, osMajor, browserMajor };
}

const MIN = {
  iOS: 13,
  Android: 7,
  Chrome: 90,
  Edge: 90,
  Firefox: 90,
  Safari: 14,
  Opera: 76,
};

export function checkCompatibility(): CompatInfo {
  if (typeof window === "undefined" || typeof navigator === "undefined") {
    return { compatible: true, browser: "ssr", os: "ssr", version: "" };
  }

  const missing = featureChecks();
  const { browser, os, version, osMajor, browserMajor } = parseUA(
    navigator.userAgent || "",
  );

  if (missing) {
    return {
      compatible: false,
      browser,
      os,
      version,
      reason: `Funcionalidade essencial em falta: ${missing}`,
    };
  }

  if (os === "iOS" && osMajor > 0 && osMajor < MIN.iOS) {
    return { compatible: false, browser, os, version, reason: `iOS ${osMajor} é muito antigo (mínimo: ${MIN.iOS}).` };
  }
  if (os === "Android" && osMajor > 0 && osMajor < MIN.Android) {
    return { compatible: false, browser, os, version, reason: `Android ${osMajor} é muito antigo (mínimo: ${MIN.Android}).` };
  }

  const min = (MIN as Record<string, number>)[browser];
  if (min && browserMajor > 0 && browserMajor < min) {
    return {
      compatible: false,
      browser,
      os,
      version,
      reason: ct("tooOld", { browser, version: browserMajor, min }),
    };
  }

  return { compatible: true, browser, os, version };
}


/** Minimal self-contained i18n for the pre-React incompatibility screen. */
const COMPAT_STRINGS: Record<string, Record<string, string>> = {
  pt: {
    heading: "O seu dispositivo ou navegador é muito antigo para usar a BeraStays.",
    body: "Para garantir segurança, rapidez e uma boa experiência, atualize o navegador ou use um dispositivo mais recente.",
    date: "Data", browser: "Navegador", system: "Sistema", reason: "Motivo",
    retry: "Tentar novamente", update: "Atualizar navegador",
    tooOld: "{{browser}} {{version}} é muito antigo (mínimo: {{min}}).",
  },
  en: {
    heading: "Your device or browser is too old to use BeraStays.",
    body: "To keep things secure, fast and pleasant to use, please update your browser or use a newer device.",
    date: "Date", browser: "Browser", system: "System", reason: "Reason",
    retry: "Try again", update: "Update browser",
    tooOld: "{{browser}} {{version}} is too old (minimum: {{min}}).",
  },
  fr: {
    heading: "Votre appareil ou navigateur est trop ancien pour utiliser BeraStays.",
    body: "Pour garantir sécurité, rapidité et confort d'utilisation, mettez à jour votre navigateur ou utilisez un appareil plus récent.",
    date: "Date", browser: "Navigateur", system: "Système", reason: "Raison",
    retry: "Réessayer", update: "Mettre à jour le navigateur",
    tooOld: "{{browser}} {{version}} est trop ancien (minimum : {{min}}).",
  },
  zh: {
    heading: "您的设备或浏览器版本过旧，无法使用 BeraStays。",
    body: "为了确保安全、流畅和良好的体验，请更新浏览器或使用较新的设备。",
    date: "日期", browser: "浏览器", system: "系统", reason: "原因",
    retry: "重试", update: "更新浏览器",
    tooOld: "{{browser}} {{version}} 版本过旧（最低要求：{{min}}）。",
  },
};

function compatLang(): string {
  let stored: string | null = null;
  try {
    stored = localStorage.getItem("berastays-language") || localStorage.getItem("i18nextLng");
  } catch {
    stored = null;
  }
  const candidates = [stored, ...(typeof navigator !== "undefined" ? [navigator.language] : [])];
  for (const c of candidates) {
    const base = (c || "").split("-")[0].toLowerCase();
    if (COMPAT_STRINGS[base]) return base;
  }
  return "en";
}

function ct(key: string, vars?: Record<string, string | number>): string {
  const lang = compatLang();
  let text = COMPAT_STRINGS[lang]?.[key] ?? COMPAT_STRINGS.en[key] ?? key;
  if (vars) {
    for (const [k, v] of Object.entries(vars)) text = text.replace(`{{${k}}}`, String(v));
  }
  return text;
}

function compatLocale(): string {
  const map: Record<string, string> = { pt: "pt-PT", en: "en-US", fr: "fr-FR", zh: "zh-CN" };
  return map[compatLang()] || "en-US";
}

/**
 * Renders a minimal incompatibility page directly into the DOM without
 * loading React. Used from main.tsx before the app mounts.
 */
export function renderIncompatibleFallback(info: CompatInfo): void {
  if (typeof document === "undefined") return;
  const year = new Date().getFullYear();
  const date = new Date().toLocaleDateString(compatLocale(), {
    day: "2-digit", month: "long", year: "numeric",
  });

  const html = `
  <div style="position:fixed;inset:0;display:flex;flex-direction:column;align-items:center;justify-content:center;background:linear-gradient(160deg,#FFF7F4 0%,#FFE7DF 100%);color:#1A1A1A;font-family:-apple-system,BlinkMacSystemFont,'Segoe UI',Roboto,sans-serif;padding:32px;text-align:center;z-index:9999;">
    <div style="width:88px;height:88px;border-radius:24px;background:#E8513D;display:flex;align-items:center;justify-content:center;box-shadow:0 12px 32px -8px rgba(232,81,61,.45);margin-bottom:24px;">
      <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="#fff" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round">
        <path d="M3 11l9-8 9 8v10a2 2 0 0 1-2 2h-4v-7H10v7H6a2 2 0 0 1-2-2V11z"/>
      </svg>
    </div>
    <h1 style="font-size:24px;font-weight:700;margin:0 0 12px;letter-spacing:-.02em;">BeraStays</h1>
    <h2 style="font-size:18px;font-weight:600;margin:0 0 16px;max-width:480px;">${ct("heading")}</h2>
    <p style="font-size:14px;line-height:1.6;color:#5A5A5A;max-width:520px;margin:0 0 24px;">
      ${ct("body")}
    </p>
    <div style="background:#fff;border:1px solid rgba(0,0,0,.06);border-radius:16px;padding:16px 20px;font-size:13px;color:#3A3A3A;margin-bottom:24px;min-width:280px;text-align:left;box-shadow:0 4px 16px -4px rgba(0,0,0,.06);">
      <div style="display:flex;justify-content:space-between;padding:4px 0;"><span style="color:#888;">${ct("date")}</span><strong>${date}</strong></div>
      <div style="display:flex;justify-content:space-between;padding:4px 0;"><span style="color:#888;">${ct("browser")}</span><strong>${info.browser} ${info.version}</strong></div>
      <div style="display:flex;justify-content:space-between;padding:4px 0;"><span style="color:#888;">${ct("system")}</span><strong>${info.os}</strong></div>
      ${info.reason ? `<div style="display:flex;justify-content:space-between;padding:4px 0;gap:12px;"><span style="color:#888;">${ct("reason")}</span><strong style="text-align:right;">${info.reason}</strong></div>` : ""}
    </div>
    <div style="display:flex;gap:12px;flex-wrap:wrap;justify-content:center;">
      <button onclick="location.reload()" style="background:#E8513D;color:#fff;border:none;padding:12px 24px;border-radius:12px;font-weight:600;font-size:14px;cursor:pointer;box-shadow:0 6px 16px -4px rgba(232,81,61,.4);">${ct("retry")}</button>
      <a href="https://www.google.com/intl/pt-PT/chrome/" target="_blank" rel="noopener" style="background:#fff;color:#E8513D;border:1px solid #E8513D;padding:12px 24px;border-radius:12px;font-weight:600;font-size:14px;text-decoration:none;display:inline-block;">${ct("update")}</a>
    </div>
    <div style="position:fixed;bottom:24px;left:0;right:0;text-align:center;font-size:12px;color:#999;">
      Made by Beracha Tech © ${year}
    </div>
  </div>`;

  const root = document.getElementById("root") || document.body;
  root.innerHTML = html;
}
